import type { BillingErrorModalProps } from "@v2/types/modalTypes";

import * as React from "react";

import { Button } from "@replohq/lattice/components/button/Button";
import { Modal } from "@replohq/lattice/components/modal/Modal";
import { ConnectShopifyCallout } from "@v2/components/ConnectShopifyCallout";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { useIsWorkspaceOwner } from "@v2/hooks/useIsWorkspaceOwner";
import { useModal } from "@v2/store/modalStore";
import { SHOPIFY_APP_LISTING_URL } from "@v2/utils/env";
import { routes } from "@v2/utils/router";
import { generatePath, Link } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { pluralize } from "replo-utils/lib/pluralize";

const getMessage = ({
  key,
  usage,
  isElementPublished,
}: {
  key?: string;
  usage?: { current: number; maximum: number };
  isElementPublished?: boolean;
}): { primary: string; secondary?: string } => {
  switch (key) {
    case "billingPlan.experimentActivationAttempt":
      return {
        primary: "Upgrade your workspace to launch Experiments",
        secondary:
          "Experiments are available on the Growth plan and above. Please upgrade to continue.",
      };
    case "billingPlan.analytics":
      return {
        primary: "Upgrade your workspace to access Analytics",
        secondary:
          "Access key ecommerce metrics and data insights to understand your store and your users.",
      };
    case "billingPlan.projectMembershipLimitExceeded":
      return {
        primary:
          "You've reached a limit on the collaborators allowed for this plan.",
        secondary: "Please upgrade from your current plan.",
      };
    case "billingPlan.entitlements.insufficient.elements.publishedElements":
    case "billingPlan.entitlements.insufficient.elements.publishedPages":
    case "billingPlan.entitlements.insufficient.elements.publishedSections":
    case "billingPlan.entitlements.insufficient.elements.publishedProductTemplates": {
      const elementNameSingular = exhaustiveSwitch({ type: key })({
        "billingPlan.entitlements.insufficient.elements.publishedElements":
          "item",
        "billingPlan.entitlements.insufficient.elements.publishedPages": "page",
        "billingPlan.entitlements.insufficient.elements.publishedSections":
          "section",
        "billingPlan.entitlements.insufficient.elements.publishedProductTemplates":
          "product template",
      });
      if (usage && usage.maximum !== 0) {
        const elementsToUnpublish =
          usage.current - usage.maximum + (isElementPublished ? 0 : 1);
        return {
          primary: `You've reached the limit on published ${elementNameSingular}s allowed for this plan.`,
          secondary: `Please upgrade from your current plan or unpublish ${elementsToUnpublish} ${pluralize(elementNameSingular, elementsToUnpublish)} to continue.`,
        };
      }
      return {
        primary: `Upgrade to publish this ${elementNameSingular}`,
      };
    }
    case "billingPlan.entitlements.insufficient.integrations.shopify": {
      const isAtOrOverLimit = usage && usage.current >= usage.maximum;
      if (isAtOrOverLimit) {
        const storesToDisconnect = usage.current - usage.maximum + 1;
        return {
          primary:
            "You've reached the limit of the number of Shopify stores you can connect.",
          secondary: `Please upgrade from your current plan or disconnect ${pluralize("store", storesToDisconnect)} to continue.`,
        };
      }
      return {
        primary:
          "You've hit the integration limit with your current billing method",
        secondary:
          "To have more integrations available on your plan, please contact support via chat or support@replo.app to enable these integrations.",
      };
    }
    default:
  }
  return {
    primary: "Upgrade Required",
    secondary:
      "This feature requires an upgraded plan. Please contact your workspace owner or upgrade your plan.",
  };
};

export const BillingErrorModal = ({
  billingPlanMessageKey,
  usage,
  isElementPublished,
}: BillingErrorModalProps) => {
  const workspace = useCurrentWorkspace();
  const { project: currentProject } = useCurrentProjectContext();

  const modal = useModal();

  const isWorkspaceOwner = useIsWorkspaceOwner(workspace?.id);

  if (!workspace) {
    return null;
  }

  let { primary: billingHeader, secondary: billingSubheader } = getMessage({
    key: billingPlanMessageKey,
    usage,
    isElementPublished,
  });

  const shouldUserConnectShopify =
    workspace?.paymentProcessor === "shopify" &&
    !workspace?.hasShopifyIntegration &&
    isWorkspaceOwner;

  if (shouldUserConnectShopify) {
    // Note (Noah, 2026-01-08): If we don't have a project context (e.g. on
    // workspace-level billing pages), we can't set the cookie with a projectId.
    // In this case, redirect the user to the Shopify App Store to install the
    // app. After installation, they'll be able to choose which project to
    // connect to.
    return (
      <Modal
        open
        onOpenChange={(open) => {
          if (!open) {
            modal.closeModal({ type: "billingErrorModal" });
          }
        }}
        size="sm"
        title="Connect Shopify"
        description="To start a billing plan with Replo, first connect your Shopify store."
      >
        {currentProject ? (
          <ConnectShopifyCallout
            cookieValue={{
              type: "billingModal",
              workspaceId: workspace.id,
              projectId: currentProject.id,
            }}
          />
        ) : (
          <Button
            variant="primary"
            size="base"
            className="w-full"
            render={
              <a
                href={SHOPIFY_APP_LISTING_URL}
                target="_blank"
                rel="noreferrer"
              />
            }
          >
            Install Shopify App
          </Button>
        )}
      </Modal>
    );
  }

  if (!isWorkspaceOwner) {
    billingHeader =
      "You don't have permission to update the billing plan, contact your workspace owner to upgrade.";
    billingSubheader = undefined;
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) {
          modal.closeModal({ type: "billingErrorModal" });
        }
      }}
      size="sm"
      title={billingHeader}
      description={billingSubheader}
    >
      <div className="flex flex-col gap-4">
        {isWorkspaceOwner ? (
          <>
            <p className="text-muted text-sm">
              To access this feature, you'll need to upgrade your billing plan.
            </p>
            <Button
              variant="primary"
              render={
                <Link
                  to={
                    currentProject
                      ? generatePath(routes.shops.settings.workspace.billing, {
                          projectId: currentProject.id,
                        })
                      : generatePath(routes.workspace.settings.billing, {
                          workspaceId: workspace.id,
                        })
                  }
                />
              }
              onClick={() => {
                modal.closeModal({ type: "billingErrorModal" });
              }}
            >
              See Plans
            </Button>
            <Button
              variant="link"
              UNSAFE_className="text-muted text-sm"
              onClick={() => {
                return window.open(
                  "https://docs.replo.app/getting-started/plans",
                  "_blank",
                );
              }}
            >
              Learn what plan is right for you →
            </Button>
          </>
        ) : (
          <p className="text-muted text-sm">
            Contact your workspace owner to request an upgrade.
          </p>
        )}
      </div>
    </Modal>
  );
};
